import { Controller, useForm } from 'react-hook-form';

import { DrugCategory } from '@/constants/programs';
import { useOnboardingContext } from '@/contexts/onboardingContext';
import useTranslate from '@/hooks/useTranslate';
import { STEPS_PATH_TRANSLATION_KEY } from '@/pages/members/pages/Onboarding/constants';
import StepComponent from '@/pages/members/pages/Onboarding/steps/StepComponent';
import { StepProps } from '@/pages/members/pages/Onboarding/steps/types';
import getSubmittedData from '@/pages/members/pages/Onboarding/utils/getSubmittedData';
import FieldError from '@/ui/members/components/form/FieldError';
import InputGroup from '@/ui/members/components/form/InputGroup';
import TextArea from '@/ui/members/components/form/TextArea';
import Heading from '@/ui/members/components/typography/Heading/Heading';

import { RecoveryGoalFormValues } from './types';

const WhatIsYourGoalStep = ({ nodeType, uid }: StepProps) => {
  const onboardingContext = useOnboardingContext();
  const { drugCategory } = onboardingContext.data;
  const { mental_health } = DrugCategory;
  const isForSubstanceUse = drugCategory !== mental_health;
  const isForMentalHealth = drugCategory === mental_health;
  const { t } = useTranslate(`${STEPS_PATH_TRANSLATION_KEY}.WhatIsYourGoalStep`);

  const {
    control,
    handleSubmit,
    formState: { errors, isValid }
  } = useForm<RecoveryGoalFormValues>({
    mode: 'onSubmit',
    defaultValues: (getSubmittedData(
      nodeType,
      uid,
      onboardingContext.steps
    ) as RecoveryGoalFormValues) || {
      recoveryGoal: ''
    }
  });

  const getIntroParagraph = () => {
    if (isForSubstanceUse) {
      return t('substanceUseIntro');
    }

    if (isForMentalHealth) {
      return t('mentalHealthIntro');
    }

    return '';
  };

  const getPlaceholderText = () => {
    if (isForSubstanceUse) {
      return t('substanceUsePlaceholder');
    }

    if (isForMentalHealth) {
      return t('mentalHealthPlaceholder');
    }

    return '';
  };

  return (
    <StepComponent isValid={isValid} handleSubmit={handleSubmit} nodeType={nodeType} uid={uid}>
      <div className="text-center mb-6">
        <Heading className="mb-3" value={1}>
          {t('heading')}
        </Heading>

        <p className="text-base mb-3">{getIntroParagraph()}</p>
      </div>

      <InputGroup>
        <Controller
          name="recoveryGoal"
          control={control}
          render={({ field }) => (
            <TextArea
              autoFocus
              rows={4}
              id="recovery-goal"
              name="recoveryGoal"
              onChange={field.onChange}
              placeholder={getPlaceholderText()}
              value={field.value}
            />
          )}
          rules={{
            required: t('recoveryGoalRequired')
          }}
        />

        <FieldError error={errors.recoveryGoal} />
      </InputGroup>
    </StepComponent>
  );
};

export default WhatIsYourGoalStep;
